'use client';

import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { usePathname, useParams, useRouter } from 'next/navigation';

interface BackStackEntry {
  id: string;
  onBack: () => void;
  pushedHistory: boolean;
}

interface BackStackContextType {
  registerBackHandler: (id: string, onBack: () => void) => () => void;
  unregisterBackHandler: (id: string) => void;
}

const BackStackContext = createContext<BackStackContextType | null>(null);

export function BackStackProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const params = useParams();
  const waId = params?.waId as string | undefined;

  const stackRef = useRef<BackStackEntry[]>([]);
  const isProgrammaticBackRef = useRef(false);
  const exitToastTimerRef = useRef<NodeJS.Timeout | null>(null);

  const [showExitToast, setShowExitToast] = useState(false);
  const exitToastActiveRef = useRef(false);

  useEffect(() => {
    exitToastActiveRef.current = showExitToast;
  }, [showExitToast]);

  const clearExitToastTimer = useCallback(() => {
    if (exitToastTimerRef.current) {
      clearTimeout(exitToastTimerRef.current);
      exitToastTimerRef.current = null;
    }
  }, []);

  const triggerExitToast = useCallback(() => {
    clearExitToastTimer();
    exitToastActiveRef.current = true;
    setShowExitToast(true);

    // Push guard state so second back press can be caught
    if (typeof window !== 'undefined') {
      window.history.pushState({ rootExitGuard: true }, '');
    }

    exitToastTimerRef.current = setTimeout(() => {
      setShowExitToast(false);
      exitToastActiveRef.current = false;
    }, 3000);
  }, [clearExitToastTimer]);

  // Ensure root exit guard state exists on chats root view
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const isChatsRoot = pathname ? pathname.includes('/chats') && !waId : false;

    if (isChatsRoot) {
      if (!window.history.state?.rootExitGuard) {
        window.history.pushState({ rootExitGuard: true }, '');
      }
    }
  }, [pathname, waId]);

  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      if (isProgrammaticBackRef.current) {
        isProgrammaticBackRef.current = false;
        return;
      }

      // 1. Intercept open modals / submenus (topmost first)
      if (stackRef.current.length > 0) {
        const top = stackRef.current.pop();
        if (top) {
          top.onBack();
        }
        return;
      }

      // 2. If viewing a chat
      if (waId) {
        const isDirectEntry = typeof window !== 'undefined' && window.history.length <= 1;
        if (isDirectEntry && pathname) {
          const pathSegments = pathname.split('/');
          if (pathSegments.length >= 5 && pathSegments[3] === 'chats') {
            const chatsRootPath = pathSegments.slice(0, 4).join('/');
            router.push(chatsRootPath);
            return;
          }
        }
        // Otherwise, natural popstate navigation takes user back to chats root
        return;
      }

      // 3. At root chats view
      const isChatsView = pathname ? pathname.includes('/chats') : false;
      if (isChatsView) {
        if (exitToastActiveRef.current) {
          // Second back press within 3s window -> Close application
          clearExitToastTimer();
          setShowExitToast(false);
          window.close();
        } else {
          // First back press -> Show exit toast
          triggerExitToast();
        }
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      clearExitToastTimer();
    };
  }, [waId, pathname, router, clearExitToastTimer, triggerExitToast]);

  const registerBackHandler = useCallback((id: string, onBack: () => void) => {
    const existingIndex = stackRef.current.findIndex(entry => entry.id === id);
    if (existingIndex !== -1) {
      stackRef.current[existingIndex].onBack = onBack;
    } else {
      stackRef.current.push({ id, onBack, pushedHistory: true });
      window.history.pushState({ backStackId: id }, '');
    }

    return () => {
      const idx = stackRef.current.findIndex(entry => entry.id === id);
      if (idx !== -1) {
        const entry = stackRef.current[idx];
        stackRef.current.splice(idx, 1);
        if (entry.pushedHistory && typeof window !== 'undefined' && window.history.state?.backStackId === id) {
          isProgrammaticBackRef.current = true;
          window.history.back();
        }
      }
    };
  }, []);

  const unregisterBackHandler = useCallback((id: string) => {
    const idx = stackRef.current.findIndex(entry => entry.id === id);
    if (idx !== -1) {
      const entry = stackRef.current[idx];
      stackRef.current.splice(idx, 1);
      if (entry.pushedHistory && typeof window !== 'undefined' && window.history.state?.backStackId === id) {
        isProgrammaticBackRef.current = true;
        window.history.back();
      }
    }
  }, []);

  return (
    <BackStackContext.Provider value={{ registerBackHandler, unregisterBackHandler }}>
      {children}
      {showExitToast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[9999] bg-slate-900/95 text-white px-5 py-2.5 rounded-full text-xs font-semibold shadow-2xl animate-in fade-in slide-in-from-bottom-4 duration-200 border border-slate-700/50 flex items-center gap-2 pointer-events-none select-none">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-amber-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span>Presiona atrás de nuevo para salir</span>
        </div>
      )}
    </BackStackContext.Provider>
  );
}

export function useBackStack() {
  const context = useContext(BackStackContext);
  if (!context) {
    throw new Error('useBackStack must be used within a BackStackProvider');
  }
  return context;
}

export function useBackHandler(id: string, isOpen: boolean, onBack: () => void) {
  const { registerBackHandler } = useBackStack();
  const unregisterRef = useRef<(() => void) | null>(null);
  const onBackRef = useRef(onBack);

  useEffect(() => {
    onBackRef.current = onBack;
  }, [onBack]);

  useEffect(() => {
    if (isOpen) {
      unregisterRef.current = registerBackHandler(id, () => {
        onBackRef.current();
      });
    } else {
      if (unregisterRef.current) {
        unregisterRef.current();
        unregisterRef.current = null;
      }
    }

    return () => {
      if (unregisterRef.current) {
        unregisterRef.current();
        unregisterRef.current = null;
      }
    };
  }, [isOpen, id, registerBackHandler]);
}
